import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useMemo, useState } from "react";
import { closeModal, Modal, openModal, TextInput } from "risa-oasis-ui_v2";
import { ToastMessages } from "../../../../../components/toasts/toastMessages";

export interface FieldConfig {
  id: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  readOnly?: boolean;
  defaultValue?: string;
}

interface AddEmailPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (formData: Record<string, string>) => Promise<void>;
  title: string;
  modalId: string;
  field1: FieldConfig;
  field2: FieldConfig;
}

export const AddEmailPasswordModal = ({
  isOpen,
  onClose,
  onSave,
  title,
  modalId,
  field1,
  field2,
}: AddEmailPasswordModalProps) => {
  const initialFormData = useMemo(
    () => ({
      [field1.id]: field1.defaultValue || "",
      [field2.id]: field2.defaultValue || "",
    }),
    [field1, field2],
  );

  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] =
    useState<Record<string, string>>(initialFormData);

  useEffect(() => {
    if (isOpen) {
      openModal(modalId);
      setFormData(initialFormData);
    } else {
      closeModal(modalId);
    }
  }, [isOpen, modalId, initialFormData]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setFormData(initialFormData);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(formData);
      toast.success(ToastMessages.EMAIL_PASSWORD_SAVE_SUCCESS);
      resetForm();
      onClose();
    } catch (error) {
      console.error("Error saving:", error);
      toast.error(ToastMessages.EMAIL_PASSWORD_SAVE_ERROR);
      resetForm();
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const isField1Valid = field1.required
    ? formData[field1.id]?.trim() !== ""
    : true;
  const isField2Valid = field2.required
    ? formData[field2.id]?.trim() !== ""
    : true;
  const isFormValid = isField1Valid && isField2Valid;

  return (
    <Modal
      dialogId={modalId}
      title={title}
      onClose={handleClose}
      onSave={handleSave}
      saveButtonText={isSaving ? "Saving..." : "Save"}
      cancelText="Cancel"
      disableSave={isSaving || !isFormValid}
    >
      <div className="flex flex-col gap-4">
        <TextInput
          id={field1.id}
          label={field1.label}
          placeholder={field1.placeholder}
          defaultValue={field1.defaultValue}
          readOnly={field1.readOnly}
          onChange={(data) => handleInputChange(field1.id, data.value)}
          resetField={formData[field1.id] === ""}
          required={field1.required}
        />
        <TextInput
          id={field2.id}
          label={field2.label}
          placeholder={field2.placeholder}
          defaultValue={field2.defaultValue}
          readOnly={field2.readOnly}
          onChange={(data) => handleInputChange(field2.id, data.value)}
          resetField={formData[field2.id] === ""}
          required={field2.required}
        />
      </div>
    </Modal>
  );
};

export default AddEmailPasswordModal;
